import { useCallback } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { startSsoSignIn } from '@/utils/sso.util';

type SSOProviderResponse = {
  configured: boolean;
  sso_domain?: string;
  provider_name?: string;
};

export type DomainSsoResult = 'redirecting' | 'not-configured' | 'error';

/**
 * Verifies a domain has a configured SSO IdP via /auth/sso-provider before
 * redirecting to Cognito, keeping users off Cognito's hosted error page when a
 * domain isn't federated. Returns the outcome so callers render their own UI;
 * on 'redirecting' the browser navigates away.
 */
export function useDomainSsoSignIn() {
  const axios = useAxios({ doNotImpersonate: true });

  return useCallback(
    async (
      domain: string,
      redirect: string | null
    ): Promise<DomainSsoResult> => {
      try {
        const { data } = await axios.get<SSOProviderResponse>(
          '/auth/sso-provider',
          { params: { domain } }
        );

        if (!data.configured) {
          return 'not-configured';
        }
        if (!data.sso_domain) {
          console.error('SSO provider response did not include sso_domain');
          return 'error';
        }

        await startSsoSignIn({ custom: domain }, redirect, data.sso_domain);
        return 'redirecting';
      } catch (error) {
        console.error('Failed to verify SSO configuration:', error);
        return 'error';
      }
    },
    [axios]
  );
}
